import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { emberApi, type TreeNode } from '../api';
import { track, trackFailed } from '../../../../../shared-web/track';
import './TreeSearch.css';

/**
 * The pause after the last keystroke before the words are searched — here and
 * in the home composer's list (HeroComposer). 280 ms until 2026-10-21: that is
 * inside a typing rhythm, so a long sentence searched at nearly every key —
 * prod 10-20, one browser 474 searches in eight minutes, each a Vertex
 * embedding and three queries on the edge, more CPU than all the chat polling
 * (Riley, prod audit; 1.0 s would have cut the hour's 599 to 167). 1200 ms is
 * past a pause in typing and well short of a wait: the list still follows the
 * words, and Enter still asks at once. The same words are not searched twice
 * either way (lastRun).
 *
 * 700 ms since 2026-10-22 (Riley): at 1200 the list took over a second to
 * show (the settle, then ~250 ms on prod). The edge now answers the same
 * words from its ten-minute cache, so the shorter settle costs less than it
 * did on 10-20.
 */
export const SEARCH_SETTLE_MS = 700;

/**
 * Find a light by what someone would type (§4.17, 2026-09-06 拍板). The
 * search answers with a short list of the lights it could mean — only those
 * related enough to be worth a look — and the person chooses; the tree walks
 * to the chosen light only then. Nothing here is anyone's Ember: a row is a
 * light's name and how many stood there. The list always ends with the way
 * out (2026-09-08 走查): none of these is yours — say it, and the tree gets
 * that light. Before this, a list that missed was a dead end, and an empty
 * search only said so.
 */
export function TreeSearch({ onPick, onTell }: { onPick: (node: TreeNode) => void; onTell: () => void }): ReactElement {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  // The latest run wins: a slower answer to an earlier text is dropped.
  const runSeq = useRef(0);
  // The last text run and its answer (2026-10-21 Riley): the field closes on
  // a tap elsewhere and opens again with its words, and Enter re-runs what
  // the settle already asked — each time a new search of the same text
  // (prod 10-20: the identical words from one browser, 63 times in an hour).
  // The stored answer stands in, counted as no run; a text that changed
  // searches as before. A failed run is not stored.
  const lastRun = useRef<{ words: string; nodes: TreeNode[] } | null>(null);
  // One load_failed per opening of the field. The list follows every
  // keystroke, so a phone that loses its signal for a minute used to write a
  // failure per letter typed (prod 9/14–10/4: 361 rows from 7 Android
  // browsers, 82–105 in one sitting each, no 5xx on the edge in those
  // windows) — a count of keystrokes, not of people who could not search.
  const failedSent = useRef(false);
  // null: nothing searched yet (or the text changed since); [] : searched, no light close enough.
  const [results, setResults] = useState<TreeNode[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    failedSent.current = false;
  }, [open]);

  const closeField = (): void => { setOpen(false); setResults(null); };

  // A tap anywhere else puts the field away, list and all (2026-09-08: on a
  // phone there is no Escape, and a field left open with words in it kept
  // the row to itself). The words stay; opening again brings them back.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent): void => {
      if (!hostRef.current?.contains(e.target as Node)) closeField();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** One search: the lights the words could mean, or the one signal that none is close enough. */
  const run = async (text: string): Promise<void> => {
    const seq = ++runSeq.current;
    const words = text.replace(/\s+/g, ' ');
    const last = lastRun.current;
    if (last && last.words === words) { setResults(last.nodes); return; }
    track('ui.clicked', { name: 'tree_search_run' });
    try {
      const { nodes } = await emberApi.treeSearch(text);
      if (seq !== runSeq.current) return;
      // Nothing over the relevance floor: the one signal that a light is
      // wanted and missing. Imperative, so every empty search counts — the
      // seen hook would fold a visit's empties into one.
      if (nodes.length === 0) track('ui.seen', { name: 'tree_search_empty' });
      lastRun.current = { words, nodes };
      setResults(nodes);
    } catch {
      if (seq !== runSeq.current) return;
      if (!failedSent.current) {
        failedSent.current = true;
        trackFailed('tree_search', 'load_failed');
      }
      setResults([]);
    }
  };

  // As the person types (2026-09-06 拍板): the list follows the words after a
  // settle (SEARCH_SETTLE_MS), no button to press. Enter only shortens the wait.
  useEffect(() => {
    const text = q.trim();
    if (!open || !text) return undefined;
    const t = window.setTimeout(() => { void run(text); }, SEARCH_SETTLE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    const text = q.trim();
    if (text) void run(text);
  };

  const pick = (node: TreeNode): void => {
    closeField();
    onPick(node);
  };

  return (
    <div className={`tree-search${open ? ' is-open' : ''}`} ref={hostRef}>
      {open ? (
        <form className="tree-search-form" onSubmit={submit} role="search">
          <input
            ref={inputRef}
            className="tree-search-input"
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); if (!e.target.value.trim()) setResults(null); }}
            placeholder="Find a place on the tree"
            aria-label="Find a place on the tree"
            maxLength={120}
            enterKeyHint="search"
            onKeyDown={(e) => { if (e.key === 'Escape') closeField(); }}
            onBlur={() => { if (!q.trim()) closeField(); }}
          />
          {q ? (
            <button type="button" className="tree-search-clear" aria-label="Clear" onClick={() => { setQ(''); setResults(null); inputRef.current?.focus(); }}>
              <ClearGlyph />
            </button>
          ) : null}
        </form>
      ) : (
        <button type="button" className="tree-search-btn" aria-label="Find a place on the tree" data-log="tree_search_open" onClick={() => setOpen(true)}>
          <SearchGlyph />
        </button>
      )}
      {open && results !== null ? (
        <div className="tree-search-results" role="listbox" aria-label="Places on the tree">
          {results.map((node) => (
            <button
              key={node.id}
              type="button"
              role="option"
              aria-selected={false}
              className="tree-search-row"
              data-log="tree_search_pick"
              data-log-node-id={node.id}
              onClick={() => pick(node)}
            >
              <span className="tree-search-row-light" aria-hidden />
              <span className="tree-search-row-title">{node.title}</span>
              <small className="tree-search-row-people" aria-label={`${node.people_count} people have stood here`}>
                <PersonGlyph />
                {node.people_count.toLocaleString('en-US')}
              </small>
            </button>
          ))}
          <button
            type="button"
            className="tree-search-tell"
            data-log="tree_search_tell"
            onClick={() => { closeField(); onTell(); }}
          >
            <FlameGlyph />
            <span>{results.length === 0 ? 'No light there yet — you may be the first. ' : 'None of these? '}<b>Tell it yourself <i aria-hidden>→</i></b></span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ClearGlyph(): ReactElement {
  return <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>;
}

function SearchGlyph(): ReactElement {
  return <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>;
}

function FlameGlyph(): ReactElement {
  return <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M12 3c1 3 4 4.5 4 8.5a4 4 0 0 1-8 0c0-1.5.6-2.6 1.4-3.5.2 1.2.9 2 1.6 2.3C11.4 8.6 11.6 5.5 12 3z" /></svg>;
}

function PersonGlyph(): ReactElement {
  return <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="8" r="4" /><path d="M5 21a7 7 0 0 1 14 0" /></svg>;
}
